import { useState } from "react";
import { PanelLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { usePlatformDesigner } from "@/lib/usePlatformDesigner";
import { formatApiError } from "@/lib/api";
import { updateBuyerModuleSidebarPreference, useUiPreferences } from "@/lib/useUiPreferences";

function useCanEditUiPreferences() {
  const { user } = useAuth();
  const isPlatformDesigner = usePlatformDesigner();
  return Boolean(
    isPlatformDesigner
    || user?.is_admin_owner
    || (user?.is_buyer_tenant && user?.role === "buyer_admin"),
  );
}

/**
 * Acceso rápido en la barra del módulo para mostrar u ocultar el menú lateral operativo.
 */
export default function ModuleSidebarQuickToggle() {
  const canEdit = useCanEditUiPreferences();
  const { buyerModuleSidebarEnabled, loading } = useUiPreferences();
  const [saving, setSaving] = useState(false);

  if (!canEdit) return null;

  const onToggle = async () => {
    const next = !buyerModuleSidebarEnabled;
    setSaving(true);
    try {
      await updateBuyerModuleSidebarPreference(next);
      toast.success(
        next
          ? "Menú lateral activado en módulos operativos."
          : "Menú lateral oculto (vista con tarjetas).",
      );
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo guardar la preferencia");
    } finally {
      setSaving(false);
    }
  };

  const enabled = buyerModuleSidebarEnabled;
  const label = enabled ? "Ocultar menú lateral" : "Mostrar menú lateral";

  return (
    <Button
      type="button"
      variant={enabled ? "default" : "outline"}
      size="sm"
      className={
        enabled
          ? "h-8 text-xs rounded-sm bg-[#0F2D5C] hover:bg-[#0F2D5C]/90"
          : "h-8 text-xs rounded-sm border-[#0F2D5C]/20 text-[#0F2D5C] hover:bg-slate-50"
      }
      title={label}
      aria-pressed={enabled}
      disabled={loading || saving}
      onClick={onToggle}
      data-testid="module-toolbar-sidebar-toggle"
    >
      <PanelLeft className="w-4 h-4 mr-1.5 shrink-0" strokeWidth={1.8} />
      {enabled ? "Menú lateral" : "Mostrar menú"}
    </Button>
  );
}
